/**
 * Kullanıcılar ekranının F1 eklerinin metinleri (durum ölçüleri, süzgeç, departman ağacı, kullanıcı tavanı). Ekranın asıl metni
 * `i18n/tr.ts` → `tr.org` içindedir; bu dosya yalnız F1'de eklenenleri taşır. Resmî "siz" dili.
 */
import type { UserListItem } from '@castinflow/shared';

export type UserStatusFilter = UserListItem['status'];

export const orgF1Text = {
  metricsTitle: 'Kullanıcı durumu',
  metric: {
    active: 'Etkin',
    invited: 'Davet bekliyor',
    suspended: 'Askıda',
    left: 'Ayrıldı',
  } satisfies Record<UserStatusFilter, string>,
  metricHint: {
    active: 'Giriş yapabilen kullanıcılar',
    invited: 'Daveti henüz kabul edilmedi',
    suspended: 'Geçici olarak giriş yapamaz; kayıtları korunur',
    left: 'Ayrılan kullanıcılar; kayıtları devredilebilir',
  } satisfies Record<UserStatusFilter, string>,
  filterActive: (what: string, n: number) => `Süzgeç: ${what} (${String(n)} kullanıcı)`,
  filterDept: (name: string) => `${name} departmanı`,
  filterClear: 'Süzgeci kaldır',
  filterUnknownDept: 'Departman bulunamadı',
  deptUserCount: (n: number) => `${String(n)} kullanıcı`,
  deptUserLink: (name: string, n: number) => `${name}: ${String(n)} kullanıcı. Listeyi aç`,
  seatTitle: (used: number, limit: number) =>
    `Kullanıcı tavanı: ${String(used)} / ${String(limit)}`,
  seatFull:
    'Tavan dolu: yeni davet reddedilir. Ayrılan kullanıcılar tavana sayılmaz; etkin, askıdaki ve daveti bekleyen kullanıcılar sayılır.',
  seatNear: (left: number) =>
    `Tavana ${String(left)} kullanıcı kaldı. Ayrılan kullanıcılar tavana sayılmaz; etkin, askıdaki ve daveti bekleyen kullanıcılar sayılır.`,
  seatPlanLink: 'Plan ve kullanım',
};

/** Adres süzgeci (`/org?durum=…&departman=…`): kart sayısı = süzülmüş tablo satırı (aynı istemci listesi) */
export const ORG_STATUS_PARAM = 'durum';
export const ORG_DEPT_PARAM = 'departman';
export const ORG_STATUS_FILTERS: readonly UserStatusFilter[] = [
  'active',
  'invited',
  'suspended',
  'left',
];

export function parseStatusFilter(v: string | null): UserStatusFilter | null {
  return v !== null && (ORG_STATUS_FILTERS as readonly string[]).includes(v)
    ? (v as UserStatusFilter)
    : null;
}

/** Departmanları ağaç sırasına dizer (üst → alt; kardeşler sunucunun sırasıyla). Sunucu düz sırada (sıra, ad) döndürür. */
export function departmentTreeOrder<T extends { id: string; parentId: string | null }>(
  list: readonly T[],
): T[] {
  const ids = new Set(list.map((d) => d.id));
  const children = new Map<string | null, T[]>();
  for (const d of list) {
    const p = d.parentId !== null && ids.has(d.parentId) ? d.parentId : null;
    const arr = children.get(p) ?? [];
    arr.push(d);
    children.set(p, arr);
  }
  const out: T[] = [];
  const seen = new Set<string>();
  const walk = (p: string | null) => {
    for (const d of children.get(p) ?? []) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      out.push(d);
      walk(d.id);
    }
  };
  walk(null);
  // Döngü ya da kopuk kayıt: kaybolmasın, sona eklenir
  for (const d of list) if (!seen.has(d.id)) out.push(d);
  return out;
}
